import React from 'react';
import { PlatformConfirmDialog } from '@/components/shared/platform-confirm-dialog';

/** Options accepted by the imperative `confirm({...})` call. Mirrors the
 *  props of the underlying PlatformConfirmDialog minus the open/state
 *  plumbing — the hook owns those. */
export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

/** A promise-returning replacement for `window.confirm`. Renders the
 *  shared platform-aware modal (iOS / Android / desktop skin).
 *
 *  Usage:
 *    const confirm = useConfirm();
 *    if (!(await confirm({ title: 'Delete?', destructive: true }))) return;
 *    doMutation();
 *
 *  Resolves true if the user clicks Confirm, false if they cancel /
 *  press Escape / click outside.
 */
type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<PendingConfirm | null>(null);

  const confirm = React.useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  // Settle the promise when the dialog closes — on confirm with `true`,
  // otherwise (cancel / Escape / click-outside) with `false`. Calling
  // `resolve` is idempotent so both paths converge on a single result.
  const settle = React.useCallback((result: boolean) => {
    setPending((cur) => {
      if (cur) cur.resolve(result);
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <PlatformConfirmDialog
        open={!!pending}
        onOpenChange={(o) => { if (!o) settle(false); }}
        title={pending?.options.title ?? ''}
        description={pending?.options.description}
        confirmLabel={pending?.options.confirmLabel}
        cancelLabel={pending?.options.cancelLabel}
        destructive={pending?.options.destructive}
        onConfirm={() => settle(true)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    // Strict: no silent fallback to window.confirm. A missing provider
    // is a bug — the hook caller would otherwise get an unstyled
    // browser dialog that defeats the whole point.
    throw new Error('useConfirm must be used within <ConfirmProvider>');
  }
  return ctx;
}
